import { Injectable } from '@angular/core';

import { AvTelemetryEnvelope } from '../models/av-telemetry.model';
import { AvTelemetryLogService } from './av-telemetry-log.service';
import { AvTelemetrySamplerService } from './av-telemetry-sampler.service';
import { summariseTicks } from './av-telemetry-summariser';

/**
 * Drains the sampler on a fixed interval, reports an aggregate, and releases the records.
 *
 *
 * The aggregated log message is TEMPORARY — see AvTelemetryLogService. Metrics ride as JSON at
 * the END of it rather than as structured properties, because per-metric names are
 * vendor-defined and would create unbounded log fields. Kusto recovers them with `parse_json`,
 * so nothing may be appended after the payload.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetryReporterService {
  static readonly REPORT_INTERVAL_MS = 60_000;

  static readonly SUMMARY_MESSAGE_PREFIX = 'AvTelemetrySummary';
  static readonly DISRUPTION_MESSAGE_PREFIX = 'AvTelemetryEvent';

  static readonly PAYLOAD_SEPARATOR = ' | ';

  static readonly MAX_REPORTED_GROUP_KEYS = 8;

  private sampler: AvTelemetrySamplerService | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly avTelemetryLogService: AvTelemetryLogService) {}

  private get intervalLabel(): string {
    return `${AvTelemetryReporterService.REPORT_INTERVAL_MS / 60_000}min`;
  }

  start(sampler: AvTelemetrySamplerService): void {
    if (this.intervalId !== null) {
      return;
    }

    this.sampler = sampler;

    this.intervalId = setInterval(
      () => this.report(),
      AvTelemetryReporterService.REPORT_INTERVAL_MS
    );
  }

  /** Reports the tail before releasing the sampler. */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.sampler) {
      this.report();
      this.sampler = null;
    }
  }

  report(): void {
    const records = this.sampler?.drain() ?? [];
    if (!records.length) {
      return;
    }

    const ticks = records.filter((record) => record.type === 'tick');
    const events = records.filter((record) => record.type === 'event');

    this.reportSummary(records, ticks, events.length);
    events.forEach((event) => this.reportDisruption(event));
  }

  private reportSummary(
    records: readonly AvTelemetryEnvelope[],
    ticks: readonly AvTelemetryEnvelope[],
    eventCount: number
  ): void {
    const summary = summariseTicks(ticks);
    const spine = records[0];

    this.emit(
      AvTelemetryReporterService.SUMMARY_MESSAGE_PREFIX,
      `${spine.vendor} ${this.intervalLabel} aggregate, ${summary.tickCount} ticks, ${eventCount} events`,
      {
        schemaVersion: spine.schemaVersion,
        vendor: spine.vendor,
        vendorVersion: spine.vendorVersion,
        interval: this.intervalLabel,
        tickCount: summary.tickCount,
        eventCount: eventCount,
        // Session-cumulative, not per-interval. Aggregate with max(), never sum().
        droppedTotal: this.sampler?.droppedRecordCount ?? 0,
        maxGroupFanOut: summary.maxGroupFanOut,
        groupKeyCount: summary.groupKeys.length,
        groupKeys: summary.groupKeys.slice(
          0,
          AvTelemetryReporterService.MAX_REPORTED_GROUP_KEYS
        ),
        metrics: summary.metrics,
      }
    );
  }

  private reportDisruption(event: AvTelemetryEnvelope): void {
    const payload = event.payload as {
      name?: string;
      attributes?: Record<string, unknown>;
    };
    const eventName = payload?.name ?? 'unknown';

    this.emit(
      AvTelemetryReporterService.DISRUPTION_MESSAGE_PREFIX,
      `${event.vendor} ${eventName}`,
      {
        schemaVersion: event.schemaVersion,
        vendor: event.vendor,
        vendorVersion: event.vendorVersion,
        eventName: eventName,
        seq: event.seq,
        occurredAt: event.occurredAt,
        attributes: payload?.attributes ?? {},
      }
    );
  }

  /** Payload stays last so a Kusto `extract` can anchor on the trailing brace. */
  private emit(prefix: string, headline: string, payload: unknown): void {
    const message = [
      `${prefix}: ${headline}`,
      JSON.stringify(payload),
    ].join(AvTelemetryReporterService.PAYLOAD_SEPARATOR);

    this.avTelemetryLogService.send(message).subscribe({ error: () => undefined });
  }
}
